import { getDomainIcon } from "@homy/ui/icons";
import {
  AlertTriangle,
  FolderKanban,
  Store,
  Repeat,
  Receipt,
} from "lucide-react";

/**
 * App shell navigation: grouped IA (jobs-to-be-done) per kb/specs/app module relationships.
 * @typedef {{ id: string; label: string; icon: typeof Inbox; path: string; matchPath?: (pathname: string) => boolean }} NavLeaf
 * @typedef {{ type: 'group'; id: string; label: string; children: NavLeaf[] }} NavGroup
 * @typedef {{ type: 'single'; items: NavLeaf[] }} NavSingle
 */

function maintenanceQueueMatch(pathname) {
  return (
    pathname === "/maintenance" ||
    (pathname.startsWith("/maintenance/") &&
      !pathname.startsWith("/maintenance/projects") &&
      !pathname.startsWith("/maintenance/vendors") &&
      !pathname.startsWith("/maintenance/services") &&
      !pathname.startsWith("/maintenance/statements"))
  );
}

/**
 * @param {Function} t
 * @param {{ role?: string } | null | undefined} user
 * @param {string | null} [currentView]
 * @param {number} [unreadInboxCount]
 * @returns {(NavSingle | NavGroup)[]}
 */
export function buildAppNavGroups(tOrOpts, user, currentView = null, unreadInboxCount = 0) {
  const isOptsObj = typeof tOrOpts === "object" && tOrOpts !== null;
  const t = typeof tOrOpts === "function" ? tOrOpts : (isOptsObj && typeof tOrOpts.t === "function" ? tOrOpts.t : ((k, fb) => fb || k));
  const resolvedUser = isOptsObj ? (tOrOpts.user || (tOrOpts.role ? { role: tOrOpts.role } : user)) : user;
  const resolvedView = isOptsObj ? (tOrOpts.viewMode || tOrOpts.currentView || currentView) : currentView;
  const resolvedUnread = isOptsObj && tOrOpts.unreadInboxCount !== undefined ? tOrOpts.unreadInboxCount : unreadInboxCount;

  const effectiveView =
    resolvedView ||
    (resolvedUser?.role === "VENDOR"
      ? "contractor"
      : Boolean(resolvedUser?.isPlatformAdmin) ||
        ["PLATFORM_ADMIN", "SUPER_ADMIN", "ORG_ADMIN", "ADMIN", "MANAGER", "OWNER"].includes(
          resolvedUser?.role
        )
      ? "operations"
      : "resident");

  const isOps = effectiveView === "operations";
  const isVendor = effectiveView === "contractor";
  const isRes = effectiveView === "resident";

  /** @type {NavLeaf[]} */
  const maintenanceChildren = [];

  if (isVendor) {
    maintenanceChildren.push(
      {
        id: "maintenanceRequests",
        label: t("nav.maintenanceRequests", "Work Orders"),
        icon: getDomainIcon("maintenance"),
        path: "/maintenance",
        matchPath: maintenanceQueueMatch,
      },
      {
        id: "vendorStatements",
        label: t("nav.vendorStatements", "Statements"),
        icon: Receipt,
        path: "/maintenance/statements",
        matchPath: (pathname) =>
          pathname.startsWith("/maintenance/statements") ||
          pathname.startsWith("/vendor/statements"),
      },
      {
        id: "trust",
        label: t("nav.trust", "Pro Verification"),
        icon: getDomainIcon("trust"),
        path: "/trust",
      }
    );
  } else if (isOps) {
    maintenanceChildren.push(
      {
        id: "maintenanceRequests",
        label: t("nav.maintenanceRequests", "Maintenance Queue"),
        icon: getDomainIcon("maintenance"),
        path: "/maintenance",
        matchPath: maintenanceQueueMatch,
      },
      {
        id: "maintenanceProjects",
        label: t("nav.projects", "Projects"),
        icon: FolderKanban,
        path: "/maintenance/projects",
        matchPath: (pathname) =>
          pathname.startsWith("/maintenance/projects"),
      },
      {
        id: "standingServices",
        label: t("nav.standingServices", "Standing Services"),
        icon: Repeat,
        path: "/maintenance/services",
        matchPath: (pathname) =>
          pathname.startsWith("/maintenance/services"),
      },
      {
        id: "vendors",
        label: t("nav.vendors", "Vendors"),
        icon: Store,
        path: "/maintenance/vendors",
        matchPath: (pathname) => pathname.startsWith("/maintenance/vendors"),
      },
      {
        id: "marketplace",
        label: t("nav.marketplace", "Marketplace"),
        icon: Store,
        path: "/marketplace",
        matchPath: (pathname) => pathname.startsWith("/marketplace"),
      },
      {
        id: "vendorStatements",
        label: t("nav.vendorStatements", "Statements"),
        icon: Receipt,
        path: "/maintenance/statements",
        matchPath: (pathname) =>
          pathname.startsWith("/maintenance/statements") ||
          pathname.startsWith("/vendor/statements"),
      }
    );
  } else {
    // Resident
    maintenanceChildren.push({
      id: "maintenanceRequests",
      label: t("nav.maintenanceRequests", "Repairs"),
      icon: getDomainIcon("maintenance"),
      path: "/maintenance",
      matchPath: maintenanceQueueMatch,
    });
  }

  const singleItems = [
    {
      id: "inbox",
      label: isRes
        ? t("nav.home", "Home")
        : t("nav.inbox", "Inbox"),
      icon: isRes ? getDomainIcon("properties") : getDomainIcon("inbox"),
      path: "/",
      matchPath: (pathname) => pathname === "/",
      badge: unreadInboxCount,
    },
    {
      id: "calendar",
      label: t("nav.calendar", "Calendar"),
      icon: getDomainIcon("calendar"),
      path: "/calendar",
      matchPath: (pathname) =>
        pathname.startsWith("/calendar") || pathname.startsWith("/vendor/calendar"),
    },
  ];

  const groups = [
    {
      type: "single",
      items: singleItems,
    },
  ];

  if (isOps) {
    groups.push(
      {
        type: "group",
        id: "portfolio",
        label: t("nav.group.portfolioMarketing", "Portfolio"),
        children: [
          {
            id: "listings",
            label: t("nav.listings", "Listings"),
            icon: getDomainIcon("listings"),
            path: "/listings",
          },
          {
            id: "properties",
            label: t("nav.properties", "Properties"),
            icon: getDomainIcon("properties"),
            path: "/properties",
          },
          {
            id: "landingPage",
            label: t("nav.landingPage", "Landing Page"),
            icon: getDomainIcon("landingPage"),
            path: "/landing-page",
          },
        ],
      },
      {
        type: "group",
        id: "leasing",
        label: t("nav.group.leasing", "Leasing & CRM"),
        children: [
          {
            id: "pipelines",
            label: t("nav.pipelines", "Pipelines"),
            icon: FolderKanban,
            path: "/pipelines",
            matchPath: (pathname) => pathname.startsWith("/pipelines"),
          },
          {
            id: "applications",
            label: t("nav.applications", "Applications"),
            icon: getDomainIcon("applications"),
            path: "/applications",
          },
          {
            id: "rentals",
            label: t("nav.rentals", "Rentals"),
            icon: getDomainIcon("rentals"),
            path: "/rentals",
          },
        ],
      }
    );
  } else if (isRes) {
    groups.push({
      type: "group",
      id: "leasing",
      label: t("nav.group.leasing", "My Tenancy"),
      children: [
        {
          id: "tenantLease",
          label: t("nav.tenantLease", "My Lease"),
          icon: getDomainIcon("rentals"),
          path: "/tenant/lease",
        },
        {
          id: "tenantPayments",
          label: t("nav.tenantPayments", "Payments"),
          icon: getDomainIcon("payments"),
          path: "/tenant/payments",
        },
        {
          id: "trust",
          label: t("nav.trust", "Trust Profile"),
          icon: getDomainIcon("trust"),
          path: "/trust",
        },
      ],
    });
  }

  groups.push({
    type: "group",
    id: "maintenance",
    label: t("nav.group.maintenance", "Maintenance"),
    children: maintenanceChildren,
  });

  const workCommsChildren = [
    {
      id: "tasks",
      label: t("nav.tasks", "Tasks"),
      icon: getDomainIcon("tasks"),
      path: "/tasks",
    },
  ];

  if (isOps) {
    workCommsChildren.push(
      {
        id: "support",
        label: t("nav.support", "Support"),
        icon: getDomainIcon("support"),
        path: "/support",
        matchPath: (pathname) =>
          pathname === "/support" ||
          (pathname.startsWith("/support/") && !pathname.startsWith("/support/help-center")),
      },
      {
        id: "issues",
        label: t("nav.issues", "Issues"),
        icon: AlertTriangle,
        path: "/issues",
      },
      {
        id: "trust",
        label: t("nav.trust", "Trust & Screening"),
        icon: getDomainIcon("trust"),
        path: "/trust",
      }
    );
  } else if (isRes) {
    workCommsChildren.push({
      id: "support",
      label: t("nav.support", "Support"),
      icon: getDomainIcon("support"),
      path: "/tenant/support",
      matchPath: (pathname) =>
        pathname.startsWith("/tenant/support") || pathname.startsWith("/resident/support"),
    });
  }

  groups.push({
    type: "group",
    id: "workComms",
    label: t("nav.group.workComms", "Work & Comms"),
    children: workCommsChildren,
  });

  if (isOps) {
    groups.push({
      type: "group",
      id: "dataOrg",
      label: t("nav.group.dataOrg", "Organization & Data"),
      children: [
        {
          id: "databases",
          label: t("nav.databases", "Databases"),
          icon: getDomainIcon("databases"),
          path: "/databases",
        },
        {
          id: "workflows",
          label: t("workflows.configTitle", "Workflow Config"),
          icon: getDomainIcon("settings"),
          path: "/applications/workflows",
          matchPath: (pathname) =>
            pathname.startsWith("/applications/workflows"),
        },
        {
          id: "organizations",
          label: t("nav.organizations", "Organizations"),
          icon: getDomainIcon("organizations"),
          path: "/organizations",
        },
      ],
    });
  }

  return groups;
}

/**
 * @param {string} pathname
 * @param {{ role?: string } | null | undefined} user
 * @param {string | null} [currentView]
 * @returns {Set<string>}
 */
export function computeActiveGroupIds(pathname, user, currentView = null) {
  const ids = new Set();
  if (
    pathname.startsWith("/listings") ||
    pathname.startsWith("/properties") ||
    pathname.startsWith("/landing-page")
  ) {
    ids.add("portfolio");
  }
  if (
    pathname.startsWith("/pipelines") ||
    (pathname.startsWith("/applications") &&
      !pathname.startsWith("/applications/workflows")) ||
    pathname.startsWith("/rentals") ||
    pathname.startsWith("/tenant/lease") ||
    pathname.startsWith("/tenant/payments")
  ) {
    ids.add("leasing");
  }
  if (
    pathname.startsWith("/maintenance") ||
    pathname.startsWith("/vendor") ||
    pathname.startsWith("/marketplace")
  ) {
    ids.add("maintenance");
  }
  if (
    pathname.startsWith("/tasks") ||
    (pathname.startsWith("/support") && !pathname.startsWith("/support/help-center")) ||
    pathname.startsWith("/tenant/support") ||
    pathname.startsWith("/resident/support") ||
    pathname.startsWith("/issues")
  ) {
    ids.add("workComms");
  }
  if (
    pathname.startsWith("/databases") ||
    pathname.startsWith("/applications/workflows") ||
    pathname.startsWith("/organizations")
  ) {
    ids.add("dataOrg");
  }
  if (pathname.startsWith("/trust")) {
    const effectiveView =
      currentView ||
      (user?.role === "VENDOR"
        ? "contractor"
        : Boolean(user?.isPlatformAdmin) ||
          ["PLATFORM_ADMIN", "SUPER_ADMIN", "ORG_ADMIN", "ADMIN", "MANAGER", "OWNER"].includes(
            user?.role
          )
        ? "operations"
        : "resident");
    if (effectiveView === "contractor") {
      ids.add("maintenance");
    } else if (effectiveView === "resident") {
      ids.add("leasing");
    } else {
      ids.add("workComms");
    }
  }
  return ids;
}

/**
 * @param {NavLeaf} leaf
 * @param {string} pathname
 */
export function isNavLeafActive(leaf, pathname) {
  if (!leaf) return false;
  if (leaf.matchPath) {
    return leaf.matchPath(pathname);
  }
  return (
    pathname === leaf.path ||
    (leaf.path !== "/" && pathname.startsWith(leaf.path))
  );
}
